import {
  BidsCollection,
  ProjectViewProjectsCollection,
  ProjectTitleEditRequestCollection,
  ReviewsCollection,
  UserHasGivenFeedbackCollection,
  UserReviewRole,
} from '@freelancer/datastore/collections';
import { deepSpread } from '@freelancer/datastore/core';
import { FrontendProjectStatusApi, RoleApi } from 'api-typings/common/common';
import {
  BidCompleteStatusApi,
  ProjectStatusApi,
  ProjectSubStatusApi,
  ProjectTitleEditRequestStatusApi,
} from 'api-typings/projects/projects';
import { addMutationPropagator } from './document-creators';

export function addMutationPropagators(authUserId: number) {
  // The end project modal updates the `bids` collection, which in the real
  // datastore also updates the corresponding bid in `projectViewProject.selectedBids`.
  // Since the fake datastore has no reducer layer, we need to recreate this
  // behaviour with a mutation propagator.
  addMutationPropagator<BidsCollection, ProjectViewProjectsCollection>({
    from: 'bids',
    to: 'projectViewProjects',
    config: {
      update: {
        // The ID of the `projectViewProject` to update
        targetDocumentId: bid => bid.projectId,
        // A function that updates the ProjectViewProject appropriately, in this
        // case by merging in the delta for the selected bid
        transformer: (authUid, delta, bid, projectViewProject) => ({
          ...projectViewProject,
          selectedBids: [
            ...projectViewProject.selectedBids.map(selectedBid =>
              selectedBid.id === bid.id ? deepSpread(bid, delta) : selectedBid,
            ),
          ],
          frontendProjectStatus:
            delta.completeStatus === BidCompleteStatusApi.COMPLETE ||
            delta.completeStatus === BidCompleteStatusApi.INCOMPLETE
              ? FrontendProjectStatusApi.COMPLETE
              : projectViewProject.frontendProjectStatus,

          ...(delta.completeStatus === BidCompleteStatusApi.COMPLETE
            ? {
                status: ProjectStatusApi.CLOSED,
                subStatus: ProjectSubStatusApi.CLOSED_AWARDED,
              }
            : delta.completeStatus === BidCompleteStatusApi.INCOMPLETE
            ? {
                status: ProjectStatusApi.CLOSED,
                subStatus: ProjectSubStatusApi.CANCEL_SELLER,
              }
            : {}),
        }),
      },
    },
  });

  // When a review is submitted, the `userHasGivenFeedback` collection should be
  // updated with `feedbackLeft: true`. Logic copied from the reducer
  addMutationPropagator<ReviewsCollection, UserHasGivenFeedbackCollection>({
    from: 'reviews',
    to: 'userHasGivenFeedback',
    config: {
      push: (authUid, review) => ({
        id: `${review.context.id}-${review.toUserId}`,
        feedbackLeft: true,
        projectId: review.context.id,
        toUserId: review.toUserId,
        fromUserId: review.fromUserId,
        reviewType:
          review.role === RoleApi.FREELANCER
            ? UserReviewRole.SELLER
            : UserReviewRole.BUYER,
      }),
    },
  });

  // On a project title edit request, when the client accepts the change
  // it should update the title of the project in the datastore as well.
  addMutationPropagator<
    ProjectTitleEditRequestCollection,
    ProjectViewProjectsCollection
  >({
    from: 'projectTitleEditRequest',
    to: 'projectViewProjects',
    config: {
      update: {
        targetDocumentId: request => request.projectId,
        transformer: (authUid, delta, projectTitleEditRequest, project) => ({
          ...project,
          ...(delta.status === ProjectTitleEditRequestStatusApi.ACCEPTED
            ? { title: projectTitleEditRequest.newTitle }
            : {}),
        }),
      },
    },
  });
}
